import { IconUser, IconX } from '@tabler/icons-react';
import { Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	Chip,
	ChipGroup,
	ChipPlus,
	ChipRemove,
	ChipRow,
	DashedDivider,
	EyebrowLabel,
	JoinWithControl,
	JoinWithRow,
	PreviewRow,
	ResolveToCardBox,
} from './bindPanel.styled.ts';
import type { DataPointWithSampleRes, JoinWith } from './BindPanel.types.ts';

interface Props {
	selectedDataPoints: DataPointWithSampleRes[];
	preview: string;
	joinWith: JoinWith;
	joinWithOptions: { value: JoinWith; label: string }[];
	onRemoveDataPoint: (path: string) => void;
	onChangeJoinWith: (next: JoinWith) => void;
}

// The "THIS BLANK WILL RESOLVE TO" card: empty prompt before anything is
// picked, or the chips + join-with control + sample preview once one or more
// data points are selected.
export const ResolveToCard = ({
	selectedDataPoints,
	preview,
	joinWith,
	joinWithOptions,
	onRemoveDataPoint,
	onChangeJoinWith,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	if (selectedDataPoints.length === 0) {
		return (
			<ResolveToCardBox $variant="empty">
				<EyebrowLabel>
					{tMipManage('master_documents.field_binding.panel.resolve_to')}
				</EyebrowLabel>
				<Text size="sm" c="dimmed">
					{tMipManage('master_documents.field_binding.panel.resolve_to_empty')}
				</Text>
			</ResolveToCardBox>
		);
	}

	return (
		<ResolveToCardBox $variant="selected">
			<EyebrowLabel>
				{tMipManage('master_documents.field_binding.panel.resolve_to')}
			</EyebrowLabel>
			<ChipRow>
				{selectedDataPoints.map((dataPoint, index) => (
					<ChipGroup key={dataPoint.path}>
						{index > 0 && <ChipPlus size={14} />}
						<Chip>
							<IconUser size={12} />
							<Text size="xs" fw={500}>
								{dataPoint.label}
							</Text>
							<ChipRemove
								aria-label={tMipManage(
									'master_documents.field_binding.panel.remove_from_selection_aria',
									{ label: dataPoint.label }
								)}
								onClick={() => onRemoveDataPoint(dataPoint.path)}
							>
								<IconX size={10} />
							</ChipRemove>
						</Chip>
					</ChipGroup>
				))}
			</ChipRow>

			<JoinWithRow>
				<Text size="xs" c="dimmed">
					{tMipManage('master_documents.field_binding.panel.join_with')}
				</Text>
				<JoinWithControl
					fullWidth
					size="sm"
					value={joinWith}
					onChange={(value) => onChangeJoinWith(value as JoinWith)}
					data={joinWithOptions}
				/>
			</JoinWithRow>

			<DashedDivider />

			<PreviewRow>
				<Text size="xs" c="dimmed">
					{tMipManage('master_documents.field_binding.panel.preview')}
				</Text>
				<Text size="sm" fw={700} data-testid="bind-panel-preview">
					{preview || '—'}
				</Text>
			</PreviewRow>
		</ResolveToCardBox>
	);
};
